import { createClient, type SupabaseClient, type PostgrestError } from "@supabase/supabase-js"

import type Search from "../types/search"

export type { SupabaseClient }

// ─── Config ──────────────────────────────────────────────────────────────────

export interface SupabaseConfig {
  domain?: string
  environment?: string
  platform?: string
  supabaseAnonKey?: string
  supabaseUrl?: string
}

export function buildSupabaseUrl(config: SupabaseConfig): string {
  const { domain, environment, platform, supabaseUrl } = config
  if (supabaseUrl) return supabaseUrl

  // Expo inlines EXPO_PUBLIC_*; Next inlines NEXT_PUBLIC_*. Accept both so
  // consumer-mobile (and shared packages) resolve the worktree suffix (-0, …).
  const dbPrefix =
    process.env.NEXT_PUBLIC_DB_PREFIX ??
    process.env.EXPO_PUBLIC_DB_PREFIX ??
    "database"
  const env = (environment || "").trim().toLowerCase()
  const suffix =
    env === "production"
      ? ""
      : (() => {
          const raw =
            process.env.NEXT_PUBLIC_SUBDOMAIN_SUFFIX ??
            process.env.EXPO_PUBLIC_SUBDOMAIN_SUFFIX ??
            ""
          return raw ? `-${raw}` : ""
        })()

  const browserHost =
    typeof window !== "undefined" ? window.location?.hostname : undefined
  const resolvedPlatform =
    platform === "local" &&
    domain &&
    browserHost &&
    browserHost !== "localhost" &&
    !browserHost.endsWith(".localhost") &&
    (browserHost === domain || browserHost.endsWith(`.${domain}`))
      ? "desktop"
      : platform

  if (env === "production") return `https://${dbPrefix}.${domain}`
  if (resolvedPlatform === "local") return `http://${dbPrefix}.localhost:83`
  return `https://${dbPrefix}-${environment}${suffix}.${domain}`
}

// ─── Client ──────────────────────────────────────────────────────────────────

/** Storage adapter for auth session persistence (e.g. AsyncStorage on React Native). */
export type SupabaseAuthStorage = {
  getItem: (key: string) => string | Promise<string | null> | null
  removeItem: (key: string) => void | Promise<void>
  setItem: (key: string, value: string) => void | Promise<void>
}

export type InitSupabaseClientOptions = {
  auth?: {
    autoRefreshToken?: boolean
    detectSessionInUrl?: boolean
    persistSession?: boolean
    storage?: SupabaseAuthStorage
  }
}

let _client: SupabaseClient | null = null
let _authLockQueue: Promise<void> = Promise.resolve()
type AuthSessionResult = Awaited<ReturnType<SupabaseClient["auth"]["getSession"]>>

let _readSessionInflight: Promise<AuthSessionResult> | null = null

export function initSupabaseClient(
  url: string,
  anonKey: string,
  options?: InitSupabaseClientOptions,
): SupabaseClient {
  _client = createClient(url, anonKey, {
    auth: {
      ...(options?.auth ?? {}),
      // Serialize auth ops — parallel getSession + signInAnonymously can hang 15s+ (Expo web).
      lock: _supabaseAuthLockSerial,
    },
    realtime: {
      // Default 10s is too tight when Realtime tenant CDC is still warming up.
      timeout: 30_000,
    },
  })
  return _client
}

export function getClient(): SupabaseClient {
  return _client!
}

// ─── Auth helpers ─────────────────────────────────────────────────────────────

/** Deduped getSession — safe when AuthBootstrap and auth queries run together. */
export function readAuthSession(): Promise<AuthSessionResult> {
  if (!_readSessionInflight) {
    _readSessionInflight = getClient()
      .auth.getSession()
      .finally(() => {
        _readSessionInflight = null
      })
  }
  return _readSessionInflight
}

export async function getAccessToken(): Promise<string | null> {
  const {
    data: { session },
  } = await readAuthSession()
  return session?.access_token ?? null
}

export async function waitForRealtimeReady(timeoutMs = 15_000): Promise<void> {
  const client = getClient()
  client.realtime.connect()

  const started = Date.now()
  while (!client.realtime.isConnected()) {
    if (Date.now() - started > timeoutMs) {
      throw new Error("Realtime WebSocket did not connect in time")
    }
    await new Promise((resolve) => setTimeout(resolve, 100))
  }

  if (process.env.NODE_ENV === "development") {
    console.log("[supabase/realtime] WebSocket connected")
  }
}

export async function syncRealtimeAuth(): Promise<string | null> {
  const client = getClient()
  const { data: sessionData } = await client.auth.getSession()
  let accessToken = sessionData.session?.access_token

  if (!accessToken) {
    const { data: refreshed } = await client.auth.refreshSession()
    accessToken = refreshed.session?.access_token
  }

  if (accessToken) {
    await client.realtime.setAuth(accessToken)
    await waitForRealtimeReady()
    if (process.env.NODE_ENV === "development") {
      console.log("[supabase/realtime] auth token synced")
    }
  } else if (process.env.NODE_ENV === "development") {
    console.warn("[supabase/realtime] no session token — Realtime may reject postgres_changes")
  }

  return accessToken ?? null
}

// ─── Formatting helpers ───────────────────────────────────────────────────────

function _authErrorCode(error: unknown): string {
  if (!error || typeof error !== "object" || !("code" in error)) return ""
  const code = (error as { code?: unknown }).code
  return typeof code === "string" ? code.trim().toLowerCase() : ""
}

export function buildErrorMessage(error: Error | unknown): string {
  const err = error instanceof Error ? error : new Error(String(error ?? ""))
  const code = _authErrorCode(error)
  const msg = _unwrapAuthErrorMessage((err.message || "").trim())
  const lower = msg.toLowerCase()
  const haystack = `${code} ${lower}`

  if (
    code === "invalid_credentials" ||
    code === "invalid_grant" ||
    haystack.includes("invalid login credentials")
  ) {
    return "Email ou mot de passe incorrect"
  }
  if (
    lower.includes("active_auth_session") ||
    lower.includes("signup_blocked_active_session")
  ) {
    return "Vous êtes déjà connecté. Nous vous redirigeons vers votre espace…"
  }
  if (
    lower.includes("user already registered") ||
    lower.includes("already been registered") ||
    lower.includes("email address has already been registered")
  ) {
    return "Un compte existe déjà avec cet email"
  }
  if (code === "email_not_confirmed" || lower.includes("email not confirmed")) {
    return "Confirmez votre email avant de vous connecter (lien reçu par mail)."
  }
  if (
    lower.includes("password should be") ||
    lower.includes("password is known to be weak") ||
    lower.includes("weak password")
  ) {
    return "Mot de passe trop faible (8 caractères minimum)."
  }
  if (
    lower.includes("compte verrouill") ||
    lower.includes("verification attempts will be rejected") ||
    lower.includes("maximum number of password")
  ) {
    return "Compte temporairement verrouillé après trop de tentatives. Réinitialisez votre mot de passe pour débloquer l'accès."
  }
  if (
    code === "over_request_rate_limit" ||
    code === "over_email_send_rate_limit" ||
    lower.includes("rate limit") ||
    lower.includes("too many requests")
  ) {
    return "Trop de tentatives. Réessayez dans un moment."
  }
  if (
    lower.includes("error sending magic link") ||
    lower.includes("error sending recovery") ||
    (lower.includes("535") && lower.includes("authentication failed"))
  ) {
    return "L'envoi du lien par email a échoué. Connectez-vous avec votre mot de passe, ou réessayez plus tard."
  }
  if (
    lower.includes("failed to fetch") ||
    lower.includes("networkerror") ||
    lower.includes("network request failed") ||
    lower.includes("load failed")
  ) {
    return "Impossible de joindre le serveur. Vérifiez votre connexion."
  }
  if (lower.includes("signups not allowed")) {
    return "Les inscriptions sont temporairement fermées."
  }
  if (lower.includes("database error saving new user")) {
    if (lower.includes("roles") && lower.includes("config")) {
      return "Impossible de créer le compte (configuration serveur). Contactez le support."
    }
    return "Impossible de créer le compte. Réessayez ou utilisez un autre email."
  }
  if (lower.includes("duplicate key") || lower.includes("accounts_slug_key")) {
    return "Impossible de créer le compte (conflit d'identifiant). Réessayez ou utilisez un autre email."
  }
  if (
    (lower.includes("anonymous") && lower.includes("password")) ||
    code === "anonymous_provider_disabled"
  ) {
    return "Session invité encore active. Rechargez la page puis réessayez."
  }
  // Keep intentional app messages (French copy we throw ourselves).
  if (
    msg.includes("création de compte") ||
    msg.includes("connexion n'a pas") ||
    msg.includes("Vérifiez votre email") ||
    msg.includes("Compte créé")
  ) {
    return msg
  }
  if (
    lower.includes("cannot read propert") ||
    lower.includes("undefined") ||
    lower.includes("typeerror") ||
    lower.includes("referenceerror")
  ) {
    return "La soumission a échoué"
  }
  // Prefer a readable server message over the opaque fallback.
  if (msg.length > 0 && msg.length < 180 && !msg.startsWith("{")) {
    return msg
  }
  return "La soumission a échoué"
}

export function camelCasePayload<T>(payload: T | T[]): T | T[] {
  if (Array.isArray(payload)) {
    return payload.map((item) => camelCasePayload(item)) as T[]
  }
  if (payload !== null && typeof payload === "object") {
    return Object.fromEntries(
      Object.entries(payload as Record<string, unknown>).map(([k, v]) => [
        _toCamelCase(k),
        typeof v === "object" && v !== null && !Array.isArray(v)
          ? camelCasePayload(v)
          : Array.isArray(v)
            ? camelCasePayload(v)
            : v,
      ]),
    ) as T
  }
  return payload
}

export function parseTimestamp(value: string | Date | number | null | undefined): Date {
  if (value == null || value === "") return new Date(NaN)
  if (typeof value === "number") return new Date(value)
  if (value instanceof Date) return value

  let s = String(value).trim()
  if (!s) return new Date(NaN)

  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return new Date(`${s}T00:00:00.000Z`)

  if (s.includes(" ") && !s.includes("T")) s = s.replace(" ", "T")

  const hasExplicitOffset =
    /[zZ]$/.test(s) ||
    /[+-]\d{2}:\d{2}$/.test(s) ||
    /[+-]\d{4}$/.test(s) ||
    /[+-]\d{2}$/.test(s)

  if (!hasExplicitOffset && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(s)) return new Date(`${s}Z`)

  return new Date(s)
}

export function throwSupabaseError(
  error: PostgrestError | null,
  fallbackMessage = "Erreur lors du chargement",
): never {
  const err = new Error(error?.message || fallbackMessage) as Error & { status: number }
  err.status = error?.code ? Number(error.code) : 500
  throw err
}

// ─── CRUD ─────────────────────────────────────────────────────────────────────

type IlikeCapableQuery = {
  ilike: (column: string, pattern: string) => IlikeCapableQuery
  or: (filters: string) => IlikeCapableQuery
}

export function applyIlikeSearch<T extends IlikeCapableQuery>(
  query: T,
  search: Pick<Search, "key" | "value">,
  defaultColumn = "name",
): T {
  const searchTerm = search.value?.trim()
  if (!searchTerm) return query

  const { key } = search
  if (!key) return query.ilike(defaultColumn, `%${searchTerm}%`) as T
  if (typeof key === "string") return query.ilike(key, `%${searchTerm}%`) as T
  const conditions = key.map((col) => `${col}.ilike.%${searchTerm}%`).join(",")
  return query.or(conditions) as T
}

export async function deleteEntity(table: string, id: string): Promise<void> {
  const { error } = await getClient().from(table).delete().eq("id", id)
  if (error) throw error
}

export async function getEntities<T>(
  table: string,
  search: Search,
  select?: string | string[],
): Promise<T[]> {
  const { ascending, filter, ilikePrefix, limit, offset, order, value } = search
  const columns = select ? (typeof select === "string" ? select : select.join(",")) : "*"

  const isJoinEntry = (v: unknown): v is Record<string, string | number | boolean> =>
    typeof v === "object" && v !== null && !Array.isArray(v)

  const directFilter: Record<string, string | number | boolean | string[]> = {}
  const joinFilter: Record<string, Record<string, string | number | boolean>> = {}
  if (filter) {
    for (const [k, v] of Object.entries(filter)) {
      if (isJoinEntry(v)) joinFilter[k] = v
      else directFilter[k] = v as string | number | boolean | string[]
    }
  }

  let selectClause = columns
  if (Object.keys(joinFilter).length > 0) {
    const joinParts = Object.entries(joinFilter).map(([relation, eqCols]) => {
      const cols = Object.keys(eqCols).join(",")
      return `${relation}!inner(${cols})`
    })
    selectClause = `${columns},${joinParts.join(",")}`
  }

  let query = getClient().from(table).select(selectClause).range(offset, offset + limit - 1)

  if (order) {
    query = query.order(order, { ascending: ascending ?? false })
    // Secondary stable sort prevents duplicate rows across pages when the primary
    // column has ties (e.g. multiple articles created at the same timestamp).
    if (order !== "id") query = query.order("id", { ascending: false })
  }

  if (ilikePrefix?.column && ilikePrefix.prefix) {
    query = query.ilike(ilikePrefix.column, `${ilikePrefix.prefix}%`)
  }

  query = applyIlikeSearch(query, search)

  for (const [k, v] of Object.entries(directFilter)) {
    if (Array.isArray(v)) {
      if (v.length === 1) query = query.eq(k, v[0]!)
      else if (v.length > 1) query = query.in(k, v)
    } else {
      query = query.eq(k, v)
    }
  }
  for (const [relation, eqCols] of Object.entries(joinFilter)) {
    for (const [col, v] of Object.entries(eqCols)) {
      query = query.eq(`${relation}.${col}`, v)
    }
  }

  const { data, error } = await query

  if (error) throwSupabaseError(error)

  let rows = (data ?? []) as unknown as Record<string, unknown>[]
  const relationKeys = Object.keys(joinFilter)
  if (relationKeys.length > 0) {
    rows = rows.map((row) => {
      const rest = { ...row }
      relationKeys.forEach((rel) => delete rest[rel])
      return rest
    })
  }
  return camelCasePayload(rows) as T[]
}

export async function getEntity<T>(
  table: string,
  value: string,
  select?: string | string[],
): Promise<T> {
  const columns = select ? (typeof select === "string" ? select : select.join(",")) : "*"

  const { data, error } = await getClient().from(table).select(columns).eq("id", value).single()

  if (error) throwSupabaseError(error)

  return camelCasePayload(data) as T
}

export async function patchEntity<T>(
  table: string,
  id: string,
  body: T,
  select?: string | string[],
): Promise<T | void> {
  const query = getClient().from(table).update(body as any).eq("id", id)

  if (select === undefined) {
    const { error } = await query
    if (error) throw error
    return undefined
  }

  const columns = typeof select === "string" ? select : select.join(",")
  const { data, error } = await query.select(columns).single()

  if (error) throw error

  return camelCasePayload(data) as T
}

export async function postEntity<T>(
  table: string,
  body: T,
  select?: string | string[],
): Promise<T | void> {
  const query = getClient().from(table).insert(body as any)

  if (select === undefined) {
    const { error } = await query
    if (error) throw error
    return undefined
  }

  const columns = typeof select === "string" ? select : select.join(",")
  const { data, error } = await query.select(columns).single()

  if (error) throw error

  return camelCasePayload(data) as T
}

export async function putEntity<T>(
  table: string,
  body: T,
  select?: string | string[],
): Promise<T | void> {
  const query = getClient().from(table).update(body as any)

  if (select === undefined) {
    const { error } = await query
    if (error) throw error
    return
  }

  const columns = typeof select === "string" ? select : select.join(",")
  const { data, error } = await query.select(columns).single()

  if (error) throw error

  return camelCasePayload(data) as T
}

// ─────────────────────────────────────────────────────────────────────────────
// # PRIVATE HELPERS
// ─────────────────────────────────────────────────────────────────────────────

async function _supabaseAuthLockSerial<T>(
  _name: string,
  acquireTimeout: number,
  fn: () => Promise<T>,
): Promise<T> {
  let release!: () => void
  const slot = new Promise<void>((resolve) => {
    release = resolve
  })
  const waitFor = _authLockQueue
  _authLockQueue = slot

  const waitMs = acquireTimeout > 0 ? acquireTimeout : 10_000
  await Promise.race([
    waitFor,
    new Promise<void>((_, reject) => {
      setTimeout(() => reject(new Error("Supabase auth lock acquire timed out")), waitMs)
    }),
  ])

  try {
    return await fn()
  } finally {
    release()
  }
}

function _toCamelCase(str: string): string {
  return str.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase())
}

function _unwrapAuthErrorMessage(msg: string): string {
  if (!msg.startsWith("{")) return msg
  try {
    const parsed = JSON.parse(msg) as Record<string, unknown>
    for (const key of ["msg", "error_description", "message", "error"]) {
      const value = parsed[key]
      if (typeof value === "string" && value.trim()) return value.trim()
    }
  } catch {
    // Keep the original blob; the caller falls back to a generic phrase.
  }
  return msg
}
